/**
 * Facility Settings Component
 *
 * Allows users to:
 * - Add new facilities with Edge API credentials
 * - Edit existing facility configurations
 * - Test API connectivity
 * - Parse Edge URLs to extract company/facility IDs
 */

import { useState, useEffect } from 'react'
import {
  Plus, Pencil, Trash2, TestTube, Loader2, Check, X,
  Building2, Key, Link2, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../App'

export default function FacilitySettings() {
  const { user } = useAuth()
  const [facilities, setFacilities] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingFacility, setEditingFacility] = useState(null)
  const [expandedFacility, setExpandedFacility] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    edge_api_facility_id: '',
    edge_api_key: '',
    edge_api_secret: '',
    edge_company_id: '',
    edge_facility_id: ''
  })
  const [formError, setFormError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [urlToParse, setUrlToParse] = useState('')

  const canEdit = user?.role === 'admin' || user?.role === 'manager'
  const isAdmin = user?.role === 'admin'

  useEffect(() => {
    fetchFacilities()
  }, [])

  const fetchFacilities = async () => {
    try {
      const { facilities } = await api.get('/facilities')
      setFacilities(facilities)
    } catch (err) {
      console.error('Failed to fetch facilities:', err)
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      edge_api_facility_id: '',
      edge_api_key: '',
      edge_api_secret: '',
      edge_company_id: '',
      edge_facility_id: ''
    })
    setFormError(null)
    setTestResult(null)
    setUrlToParse('')
    setEditingFacility(null)
  }

  const openAddForm = () => {
    resetForm()
    setShowForm(true)
  }

  const openEditForm = (facility) => {
    setFormData({
      name: facility.name || '',
      edge_api_facility_id: facility.edge_api_facility_id || '',
      edge_api_key: facility.edge_api_key || '',
      edge_api_secret: '', // Don't pre-fill secret
      edge_company_id: facility.edge_company_id || '',
      edge_facility_id: facility.edge_facility_id || ''
    })
    setEditingFacility(facility)
    setShowForm(true)
    setFormError(null)
    setTestResult(null)
  }

  const closeForm = () => {
    setShowForm(false)
    resetForm()
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setFormError(null)
    setTestResult(null)
  }

  const parseEdgeUrl = async () => {
    if (!urlToParse) return
    try {
      const { edge_company_id, edge_facility_id } = await api.post('/facilities/parse-url', {
        url: urlToParse
      })
      setFormData(prev => ({
        ...prev,
        edge_company_id,
        edge_facility_id
      }))
      setUrlToParse('')
    } catch (err) {
      setFormError('Invalid Edge URL format')
    }
  }

  const testCredentials = async () => {
    if (!formData.edge_api_facility_id || !formData.edge_api_key || !formData.edge_api_secret) {
      setFormError('API credentials are required to test connection')
      return
    }

    setTesting(true)
    setTestResult(null)
    try {
      const result = await api.post('/facilities/test-credentials', {
        edge_api_facility_id: formData.edge_api_facility_id,
        edge_api_key: formData.edge_api_key,
        edge_api_secret: formData.edge_api_secret
      })
      setTestResult(result)
    } catch (err) {
      setTestResult({ success: false, message: err.message })
    } finally {
      setTesting(false)
    }
  }

  const saveFacility = async (e) => {
    e.preventDefault()

    if (!formData.name || !formData.edge_api_facility_id || !formData.edge_api_key) {
      setFormError('Name and API credentials are required')
      return
    }

    if (!editingFacility && !formData.edge_api_secret) {
      setFormError('API secret is required for new facilities')
      return
    }

    setSaving(true)
    setFormError(null)

    try {
      if (editingFacility) {
        // Update existing facility
        const updateData = { ...formData }
        // Only include secret if it was changed
        if (!updateData.edge_api_secret) {
          delete updateData.edge_api_secret
        }
        await api.put(`/facilities/${editingFacility.id}`, updateData)
      } else {
        // Create new facility
        await api.post('/facilities', formData)
      }
      await fetchFacilities()
      closeForm()
    } catch (err) {
      setFormError(err.message || 'Failed to save facility')
    } finally {
      setSaving(false)
    }
  }

  const deleteFacility = async (facilityId) => {
    if (!confirm('Are you sure you want to deactivate this facility?')) return

    try {
      await api.delete(`/facilities/${facilityId}`)
      await fetchFacilities()
    } catch (err) {
      alert('Failed to delete facility: ' + err.message)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facility Settings</h1>
          <p className="text-gray-500">Manage your Storable Edge facility connections</p>
        </div>
        {isAdmin && (
          <button onClick={openAddForm} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Facility
          </button>
        )}
      </div>

      {/* Facility List */}
      <div className="space-y-4">
        {facilities.length === 0 ? (
          <div className="card text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Facilities</h3>
            <p className="text-gray-500 mb-4">
              {isAdmin
                ? 'Add your first Storable Edge facility to get started.'
                : 'No facilities have been configured. Contact an administrator.'}
            </p>
            {isAdmin && (
              <button onClick={openAddForm} className="btn-primary inline-flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Facility
              </button>
            )}
          </div>
        ) : (
          facilities.map(facility => (
            <div key={facility.id} className="card p-0 overflow-hidden">
              {/* Facility Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedFacility(expandedFacility === facility.id ? null : facility.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-100">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{facility.name}</h3>
                    <p className="text-sm text-gray-500">
                      {facility.is_active ? 'Active' : 'Inactive'}
                      {facility.access_level && ` • ${facility.access_level} access`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditForm(facility); }}
                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteFacility(facility.id); }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                  {expandedFacility === facility.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Facility Details */}
              {expandedFacility === facility.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* API Credentials - Admin Only */}
                    {user?.role === 'admin' && (
                      <>
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-1">API Facility ID</p>
                          <p className="text-sm text-gray-900 font-mono">{facility.edge_api_facility_id || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-1">API Key</p>
                          <p className="text-sm text-gray-900 font-mono">{facility.edge_api_key || '-'}</p>
                        </div>
                      </>
                    )}
                    {/* Non-admin sees limited info */}
                    {user?.role !== 'admin' && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 uppercase mb-1">API Credentials</p>
                        <p className="text-sm text-gray-500 italic">Only visible to administrators</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Company ID (Web)</p>
                      <p className="text-sm text-gray-900">{facility.edge_company_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Facility ID (Web)</p>
                      <p className="text-sm text-gray-900">{facility.edge_facility_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Created</p>
                      <p className="text-sm text-gray-900">
                        {new Date(facility.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Last Updated</p>
                      <p className="text-sm text-gray-900">
                        {facility.updated_at ? new Date(facility.updated_at).toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingFacility ? 'Edit Facility' : 'Add New Facility'}
              </h2>
              <button
                onClick={closeForm}
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={saveFacility} className="p-4 space-y-6">
              {/* Error Display */}
              {formError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}

              {/* Facility Name */}
              <div>
                <label className="label">Facility Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="My Storage Facility"
                  className="input"
                  required
                />
              </div>

              {/* API Credentials Section - Admin Only */}
              {isAdmin ? (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Edge API Credentials
                  </h3>

                  <div>
                    <label className="label">API Facility UUID</label>
                    <input
                      type="text"
                      name="edge_api_facility_id"
                      value={formData.edge_api_facility_id}
                      onChange={handleInputChange}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="input font-mono"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The UUID used in API calls (different from the numeric web ID)
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">API Key (Consumer Key)</label>
                      <input
                        type="text"
                        name="edge_api_key"
                        value={formData.edge_api_key}
                        onChange={handleInputChange}
                        placeholder="Your OAuth consumer key"
                        className="input font-mono"
                        required
                      />
                    </div>
                    <div>
                      <label className="label">
                        API Secret (Consumer Secret)
                        {editingFacility && <span className="text-gray-400 font-normal"> - leave blank to keep current</span>}
                      </label>
                      <input
                        type="password"
                        name="edge_api_secret"
                        value={formData.edge_api_secret}
                        onChange={handleInputChange}
                        placeholder={editingFacility ? '••••••••' : 'Your OAuth consumer secret'}
                        className="input font-mono"
                        required={!editingFacility}
                      />
                    </div>
                  </div>

                  {/* Test Connection */}
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={testCredentials}
                      disabled={testing}
                      className="btn-secondary flex items-center gap-2"
                    >
                      {testing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                      Test Connection
                    </button>
                    {testResult && (
                      <div className={`text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="flex items-center gap-1">
                          {testResult.success ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          {testResult.message || (testResult.success ? 'Connected!' : 'Failed')}
                        </span>
                        {!testResult.success && testResult.details?.hint && (
                          <p className="text-xs text-gray-500 mt-1 ml-5">{testResult.details.hint}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4" />
                    Edge API Credentials
                  </h3>
                  <p className="text-sm text-gray-500">
                    API credentials can only be viewed and modified by administrators.
                  </p>
                </div>
              )}

              {/* Web URL IDs Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Web URL IDs (for deep links)
                </h3>

                {/* URL Parser */}
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">
                    Paste a Storable Edge URL to extract IDs automatically:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={urlToParse}
                      onChange={(e) => setUrlToParse(e.target.value)}
                      placeholder="https://www.storedgefms.com/company/123/facility/456/..."
                      className="input flex-1 text-sm"
                    />
                    <button
                      type="button"
                      onClick={parseEdgeUrl}
                      className="btn-secondary text-sm"
                    >
                      Extract
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Company ID</label>
                    <input
                      type="text"
                      name="edge_company_id"
                      value={formData.edge_company_id}
                      onChange={handleInputChange}
                      placeholder="123"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Facility ID</label>
                    <input
                      type="text"
                      name="edge_facility_id"
                      value={formData.edge_facility_id}
                      onChange={handleInputChange}
                      placeholder="456"
                      className="input"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  These numeric IDs are used to generate direct links to Storable Edge web interface.
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingFacility ? 'Save Changes' : 'Add Facility'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
